/**
 * @description
 * Main health monitoring dashboard component that combines health status and history.
 * Provides comprehensive health monitoring with scheduling and alerting capabilities.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-08
 */

import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Settings, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { DeploymentHealthStatus } from './DeploymentHealthStatus';
import { DeploymentHealthHistory } from './DeploymentHealthHistory';
import { api } from '../../lib/api';

interface HealthMonitoringProps {
  deploymentId: string;
  className?: string;
  onHealthUpdate: (health: HealthCheck | null) => void;
}

interface HealthCheck {
  overall: 'healthy' | 'degraded' | 'unhealthy';
  worker: {
    status: 'healthy' | 'degraded' | 'unhealthy';
    details: string;
    lastDeployed?: string;
    responseTime?: number;
  };
  database: {
    status: 'healthy' | 'degraded' | 'unhealthy';
    details: string;
    connected: boolean;
    responseTime?: number;
  };
  domain: {
    status: 'healthy' | 'degraded' | 'unhealthy';
    details: string;
    resolved: boolean;
    sslValid: boolean;
    responseTime?: number;
  };
  timestamp: string;
}

interface HealthHistory {
  timestamp: string;
  overall: 'healthy' | 'degraded' | 'unhealthy';
  worker: {
    status: 'healthy' | 'degraded' | 'unhealthy';
    responseTime?: number;
  };
  database: {
    status: 'healthy' | 'degraded' | 'unhealthy';
    responseTime?: number;
  };
  domain: {
    status: 'healthy' | 'degraded' | 'unhealthy';
    responseTime?: number;
  };
}

export function DeploymentHealthMonitoring({ deploymentId, className = '', onHealthUpdate }: HealthMonitoringProps) {
  const [currentHealth, setCurrentHealth] = useState<HealthCheck | null>(null);
  const [healthHistory, setHealthHistory] = useState<HealthHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval] = useState(30000); // 30 seconds

  const fetchHealthStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const health = await api.getDeploymentHealth(deploymentId);
      setCurrentHealth(health as HealthCheck);
      onHealthUpdate(health as HealthCheck);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch health status');
    } finally {
      setLoading(false);
    }
  }, [deploymentId, onHealthUpdate]);

  const fetchHealthHistory = useCallback(async () => {
    try {
      const history = await api.getDeploymentHealthHistory(deploymentId);
      setHealthHistory(history as HealthHistory[]);
    } catch (err) {
      console.error('Failed to fetch health history:', err);
    }
  }, [deploymentId]);

  const scheduleHealthChecks = async (intervalMinutes: number) => {
    try {
      await api.scheduleDeploymentHealthChecks(deploymentId, intervalMinutes);
      // Show success message
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to schedule health checks');
    }
  };

  useEffect(() => {
    fetchHealthStatus();
    fetchHealthHistory();
  }, [fetchHealthStatus, fetchHealthHistory]);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchHealthStatus();
      }, refreshInterval);

      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, fetchHealthStatus]);

  const handleRefresh = () => {
    fetchHealthStatus();
    fetchHealthHistory();
  };

  const getOverallStatusIcon = () => {
    if (!currentHealth) return <Clock className="w-5 h-5 text-gray-500" />;
    
    switch (currentHealth.overall) {
      case 'healthy':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'degraded':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'unhealthy':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {getOverallStatusIcon()}
          <h2 className="text-xl font-semibold">Health Monitoring</h2>
          {currentHealth && (
            <span className={`px-2 py-1 rounded text-sm font-medium ${
              currentHealth.overall === 'healthy' 
                ? 'bg-green-100 text-green-700' 
                : currentHealth.overall === 'degraded'
                ? 'bg-yellow-100 text-yellow-700'
                : 'bg-red-100 text-red-700'
            }`}>
              {currentHealth.overall.toUpperCase()}
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`px-3 py-2 text-sm rounded ${
              autoRefresh 
                ? 'bg-green-600 text-white hover:bg-green-700' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Auto Refresh
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Health Status */}
      {currentHealth && (
        <DeploymentHealthStatus health={currentHealth} />
      )}

      {/* Health History */}
      {healthHistory.length > 0 && (
        <DeploymentHealthHistory history={healthHistory} />
      )}

      {/* Health Check Scheduling */}
      <div className="p-4 border rounded-lg">
        <div className="flex items-center gap-2 mb-4">
          <Settings className="w-4 h-4 text-gray-500" />
          <h3 className="font-semibold">Health Check Scheduling</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => scheduleHealthChecks(5)}
            className="p-3 text-left border rounded-lg hover:bg-gray-50"
          >
            <div className="font-medium">Every 5 minutes</div>
            <div className="text-sm text-gray-600">High frequency monitoring</div>
          </button>
          
          <button
            onClick={() => scheduleHealthChecks(15)}
            className="p-3 text-left border rounded-lg hover:bg-gray-50"
          >
            <div className="font-medium">Every 15 minutes</div>
            <div className="text-sm text-gray-600">Standard monitoring</div>
          </button>
          
          <button
            onClick={() => scheduleHealthChecks(60)}
            className="p-3 text-left border rounded-lg hover:bg-gray-50"
          >
            <div className="font-medium">Every hour</div>
            <div className="text-sm text-gray-600">Low frequency monitoring</div>
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeploymentHealthMonitoring;
